import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useAxios } from '../contexts/axiosContext';

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function usernameToId(sub) {
  if (!sub) return null;
  return sub.replace(/^ADMIN_/, '').replace(/^USER_/, '');
}

function scopesToRole(scopes) {
  const list = Array.isArray(scopes)
    ? scopes
    : typeof scopes === 'string'
      ? scopes.split(',')
      : [];

  const normalized = list.map((s) => String(s).trim()).filter(Boolean);

  if (normalized.includes('ROLE_ADMIN')) return 'ADMIN';
  return 'USER';
}

export default function UserService() {
  const { publicAxios, authAxios } = useAxios();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  function login({ email, password }) {
    return new Promise((resolve, reject) => {
      publicAxios.post('/login', { email, password })
        .then((resp) => {
          const accessToken = resp?.data?.data?.accessToken;
          const refreshToken = resp?.data?.data?.refreshToken;

          if (!accessToken || !refreshToken) {
            reject(new Error('Login response missing tokens'));
            return;
          }

          const jwt = decodeJwt(accessToken);
          const role = scopesToRole(jwt?.scopes);
          const id = usernameToId(jwt?.sub);
          let user = { id, email, role };

          const afterLogin = (finalUser) => {
            auth.login({ token: accessToken, refreshToken, user: finalUser });
            const locationPath = location.state?.path || '/candidates';
            navigate(locationPath, { replace: true });
            resolve(finalUser);
          };

          afterLogin(user);
        })
        .catch((err) => reject(err));
    });
  }

  function logout() {
    auth.logout();
    navigate('/login', { replace: true });
  }

  function listUsers({ page = 0, pageSize = 15, signal } = {}) {
    return new Promise((resolve, reject) => {
      authAxios.get('/users/list', { params: { page, pageSize }, signal })
        .then((resp) => resolve(resp.data.data))
        .catch((err) => reject(err));
    });
  }

  function userDetail(id, { signal } = {}) {
    return new Promise((resolve, reject) => {
      authAxios.get(`/users/detail/${id}`, { signal })
        .then((resp) => resolve(resp.data.data))
        .catch((err) => reject(err));
    });
  }

  function updateUserRole({ id, role }) {
    return new Promise((resolve, reject) => {
      authAxios.put('/users/update', { id, role })
        .then((resp) => resolve(resp.data.data))
        .catch((err) => reject(err));
    });
  }

  function deleteUser(id) {
    return new Promise((resolve, reject) => {
      authAxios.delete(`/users/delete/${id}`)
        .then((resp) => resolve(resp.data))
        .catch((err) => reject(err));
    });
  }

  function inviteUser({ email, role = 'USER' }) {
    return new Promise((resolve, reject) => {
      authAxios.post('/users/create', { email, role })
        .then((resp) => resolve(resp.data.data))
        .catch((err) => reject(err));
    });
  }

  return { login, logout, listUsers, userDetail, updateUserRole, deleteUser, inviteUser };
}